/**
 * Has this client accepted the CURRENT declaration set?
 *
 * Acceptance is per (contact, version). An acceptance of an older version does not count: if the
 * wording changes, the client is re-prompted. That is the whole reason the version is stored.
 *
 * Staff never see this gate. They are not the applicant and make no declaration.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { declarationTexts, declarationsSha256, getDeclarationSet } from '../lib/declarations/declarationSet';
import type { DeclarationAudience } from '../lib/declarations/declarationSet';

interface UseDeclarationsResult {
  /** null while unknown — callers must not gate on `false` until loading completes. */
  hasAccepted: boolean | null;
  loading: boolean;
  error: string | null;
  accept: () => Promise<void>;
}

export function useDeclarations(
  contactId: string | null,
  exempt: boolean,
  /**
   * WHICH set they make. `null` = they make none (staff, witness, a sponsor on a visa with no
   * sponsor form). Treated as exempt: we must never fall back to the applicant set for someone who
   * is not an applicant, because the acceptance is written down as evidence.
   */
  audience: DeclarationAudience | null,
): UseDeclarationsResult {
  const [hasAccepted, setHasAccepted] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Exempt (staff, or anyone with no set to make): treat as satisfied so the gate never fires.
    if (exempt || !audience) {
      setHasAccepted(true);
      setLoading(false);
      return;
    }
    // No resolvable contact (unlinked contacts.user_id, duplicate contact rows, or a failed
    // contacts query). We cannot ask this person to declare anything, because we do not know who
    // they are.
    //
    // Resolve to `true` — NOT `null`. `null` means "still deciding", and DeclarationsGate renders
    // a full-screen spinner while it is null. Leaving it null here would hang such a user on an
    // infinite "Loading..." with no error, no redirect, and no sign-out button (PortalLayout, which
    // owns sign-out, lives INSIDE the gate). That is a total lockout, and it would catch staff too.
    //
    // Passing them through restores exactly the pre-gate behaviour: a degraded but navigable portal
    // they can sign out of. They see no forms they could have filled anyway, since the same broken
    // contact link gates the rest of the app.
    if (!contactId) {
      setHasAccepted(true);
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('client_declaration_acceptances')
        .select('id')
        .eq('contact_id', contactId)
        .eq('declaration_set_version', getDeclarationSet(audience).version)
        .limit(1);

      if (cancelled) return;
      if (err) {
        // Fail CLOSED: if we cannot prove acceptance, do not let the client past the gate.
        setError(err.message);
        setHasAccepted(false);
      } else {
        setHasAccepted((data?.length ?? 0) > 0);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [contactId, exempt, audience]);

  const accept = useCallback(async () => {
    if (!contactId) throw new Error('No contact to record an acceptance against.');
    // Refuse rather than guess. Recording the wrong set would be false evidence of what this person
    // promised, which is worse than recording nothing at all.
    if (!audience) throw new Error('No declaration set applies to this person.');

    const texts = declarationTexts(audience);
    const sha256 = await declarationsSha256(audience, texts);

    // accepted_at is a server default: never trust a client-supplied timestamp on evidence.
    const { error: err } = await supabase.from('client_declaration_acceptances').insert({
      contact_id: contactId,
      declaration_set_version: getDeclarationSet(audience).version,
      declarations_snapshot: texts,
      declarations_sha256: sha256,
      accepted_via: 'portal_setup_task',
      user_agent: navigator.userAgent,
    });

    if (err) throw new Error(err.message);
    setHasAccepted(true);
  }, [contactId, audience]);

  return { hasAccepted, loading, error, accept };
}
